import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const GOOGLE_SHEETS_TOOL_NAME = "google_sheets" as const;

export const GOOGLE_SHEETS_TOOLS_METADATA = [
  {
    name: "list_spreadsheets",
    description:
      "Search for and list your Google Sheets spreadsheets by name, across your personal and shared drives, to find a spreadsheet when you do not know its ID.",
    schema: {
      nameFilter: z
        .string()
        .optional()
        .describe(
          "Text to search for in spreadsheet names (case-insensitive prefix match)."
        ),
      pageToken: z.string().optional().describe("Page token for pagination."),
      pageSize: z
        .number()
        .optional()
        .describe("Maximum number of spreadsheets to return (max 1000)."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing Google Sheets spreadsheets",
      done: "List Google Sheets spreadsheets",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_spreadsheet",
    description:
      "Get metadata and properties of a specific Google Sheets spreadsheet.",
    schema: {
      spreadsheetId: z
        .string()
        .describe("The ID of the spreadsheet to retrieve."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Getting Google Sheets spreadsheet",
      done: "Get Google Sheets spreadsheet",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_worksheet",
    description:
      "Read the cell values from a range in a Google Sheets worksheet (tab), returning the data found in the given A1 range.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      range: z
        .string()
        .describe(
          "The A1 notation of the range to retrieve (e.g., 'Sheet1!A1:D10' or 'A1:D10')."
        ),
      majorDimension: z
        .enum(["ROWS", "COLUMNS"])
        .default("ROWS")
        .describe("The major dimension of the values."),
      valueRenderOption: z
        .enum(["FORMATTED_VALUE", "UNFORMATTED_VALUE", "FORMULA"])
        .default("FORMATTED_VALUE")
        .describe("How values should be represented in the output."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Getting Google Sheets worksheet",
      done: "Get Google Sheets worksheet",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "update_cells",
    description: "Update cells in a Google Sheets spreadsheet.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      range: z
        .string()
        .describe(
          "The A1 notation of the range to update (e.g., 'Sheet1!A1:D10')."
        ),
      values: z
        .array(z.array(z.union([z.string(), z.number(), z.boolean()])))
        .describe("The values to update. Each sub-array represents a row."),
      majorDimension: z
        .enum(["ROWS", "COLUMNS"])
        .default("ROWS")
        .describe("The major dimension of the values."),
      valueInputOption: z
        .enum(["RAW", "USER_ENTERED"])
        .default("USER_ENTERED")
        .describe("How the input data should be interpreted."),
    },
    stake: "low",
    displayLabels: {
      running: "Updating Google Sheets cells",
      done: "Update Google Sheets cells",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "append_data",
    description: "Append data to a Google Sheets spreadsheet.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      range: z
        .string()
        .describe(
          "The A1 notation of the range to append to (e.g., 'Sheet1!A1:D1')."
        ),
      values: z
        .array(z.array(z.union([z.string(), z.number(), z.boolean()])))
        .describe("The values to append. Each sub-array represents a row."),
      majorDimension: z
        .enum(["ROWS", "COLUMNS"])
        .default("ROWS")
        .describe("The major dimension of the values."),
      valueInputOption: z
        .enum(["RAW", "USER_ENTERED"])
        .default("USER_ENTERED")
        .describe("How the input data should be interpreted."),
      insertDataOption: z
        .enum(["OVERWRITE", "INSERT_ROWS"])
        .default("INSERT_ROWS")
        .describe("How the input data should be inserted."),
    },
    stake: "low",
    displayLabels: {
      running: "Appending data to Google Sheets",
      done: "Append data to Google Sheets",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "clear_range",
    description: "Clear values from a range in a Google Sheets spreadsheet.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      range: z
        .string()
        .describe(
          "The A1 notation of the range to clear (e.g., 'Sheet1!A1:D10')."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Clearing Google Sheets range",
      done: "Clear Google Sheets range",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "create_spreadsheet",
    description: "Create a new Google Sheets spreadsheet.",
    schema: {
      title: z.string().describe("The title of the new spreadsheet."),
      sheetTitles: z
        .array(z.string())
        .optional()
        .describe(
          "Titles for initial sheets. If not provided, creates one sheet with default title."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Creating Google Sheets spreadsheet",
      done: "Create Google Sheets spreadsheet",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "add_worksheet",
    description:
      "Add a new worksheet to an existing Google Sheets spreadsheet.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      title: z.string().describe("The title of the new worksheet."),
      rowCount: z
        .number()
        .optional()
        .describe("Number of rows in the new worksheet."),
      columnCount: z
        .number()
        .optional()
        .describe("Number of columns in the new worksheet."),
    },
    stake: "low",
    displayLabels: {
      running: "Adding Google Sheets worksheet",
      done: "Add Google Sheets worksheet",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "delete_worksheet",
    description: "Delete a worksheet from a Google Sheets spreadsheet.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      sheetId: z.number().describe("The ID of the worksheet to delete."),
    },
    stake: "low",
    displayLabels: {
      running: "Deleting Google Sheets worksheet",
      done: "Delete Google Sheets worksheet",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "format_cells",
    description:
      "Apply formatting to a range of cells in a Google Sheets worksheet, such as bold or italic text, font size, background color, and horizontal alignment.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      sheetId: z.number().describe("The ID of the worksheet to format."),
      startRowIndex: z.number().describe("The start row index (0-based)."),
      endRowIndex: z.number().describe("The end row index (exclusive)."),
      startColumnIndex: z
        .number()
        .describe("The start column index (0-based)."),
      endColumnIndex: z.number().describe("The end column index (exclusive)."),
      format: z
        .object({
          backgroundColor: z
            .object({
              red: z.number().min(0).max(1).optional(),
              green: z.number().min(0).max(1).optional(),
              blue: z.number().min(0).max(1).optional(),
            })
            .optional()
            .describe("Background color in RGB format (values 0-1)."),
          textFormat: z
            .object({
              bold: z.boolean().optional(),
              italic: z.boolean().optional(),
              fontSize: z.number().optional(),
            })
            .optional()
            .describe("Text formatting options."),
          horizontalAlignment: z
            .enum(["LEFT", "CENTER", "RIGHT"])
            .optional()
            .describe("Horizontal alignment."),
        })
        .describe("Formatting options to apply."),
    },
    stake: "low",
    displayLabels: {
      running: "Formatting Google Sheets cells",
      done: "Format Google Sheets cells",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "copy_sheet",
    description:
      "Copy a worksheet (tab) from one Google Sheets spreadsheet into another.",
    schema: {
      sourceSpreadsheetId: z
        .string()
        .describe(
          "The ID of the source spreadsheet containing the sheet to copy."
        ),
      sheetId: z
        .number()
        .describe("The ID of the sheet to copy from the source spreadsheet."),
      destinationSpreadsheetId: z
        .string()
        .describe(
          "The ID of the destination spreadsheet where the sheet will be copied."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Copying Google Sheets sheet",
      done: "Copy Google Sheets sheet",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "rename_worksheet",
    description: "Rename a worksheet in a Google Sheets spreadsheet.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      sheetId: z.number().describe("The ID of the worksheet to rename."),
      newTitle: z.string().describe("The new title for the worksheet."),
    },
    stake: "low",
    displayLabels: {
      running: "Renaming Google Sheets worksheet",
      done: "Rename Google Sheets worksheet",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "move_worksheet",
    description:
      "Move a worksheet to a new position in a Google Sheets spreadsheet.",
    schema: {
      spreadsheetId: z.string().describe("The ID of the spreadsheet."),
      sheetId: z.number().describe("The ID of the worksheet to move."),
      newIndex: z
        .number()
        .min(0)
        .describe(
          "The new zero-based index position for the worksheet. 0 = first position, 1 = second position, etc."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Moving Google Sheets worksheet",
      done: "Move Google Sheets worksheet",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
] as const;

export const GOOGLE_SHEETS_SERVER = {
  serverInfo: {
    name: "google_sheets",
    version: "1.0.0",
    description: "Work with spreadsheet data and tables.",
    authorization: {
      provider: "gmail" as const,
      supported_use_cases: ["personal_actions", "platform_actions"] as const,
      scope:
        "https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive.readonly" as const,
    },
    icon: "GoogleSpreadsheetLogo",
    documentationUrl: "https://docs.laufwerk.artech-is.com/tools/tools-anbinden#ein-tool-einrichten",
  },
  tools: GOOGLE_SHEETS_TOOLS_METADATA,
} as const satisfies ServerMetadata;
